// Axios instance with base URL, interceptors

import axios from 'axios';
import tokenManager from '../utils/tokenManager';
import errorHandler from '../utils/errorHandler';
import { API_TIMEOUT, DEFAULT_HEADERS, HTTP_STATUS } from '../../constants/apiConstants';
import ENDPOINTS from './endpoints';

// From .env only. CRA inlines this at start, so restart after changing it.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

if (process.env.NODE_ENV !== 'production') {
  if (!API_BASE_URL) {
    console.error('[API] REACT_APP_API_BASE_URL is not set. Add it to .env and restart the dev server.');
  } else {
    console.info(`[API] Base URL: ${API_BASE_URL}`);
  }
}

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: DEFAULT_HEADERS,
});

// Request interceptor - Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip auth for specific requests
    if (config.skipAuth) {
      return config;
    }

    // Get token from tokenManager
    const token = tokenManager.getAccessToken();
    
    // Add token to headers if it exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['x-auth-token'] = token;
    } else {
      console.warn(`[API Request] No auth token found for ${config.url}. Request may fail with 401.`);
    }

    // Log request in development
    if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, config.data);
    }

    return config;
  },
  (error) => {
    errorHandler.logError(error, 'Request Interceptor');
    return Promise.reject(error);
  }
);

// Response interceptor - Handle responses and errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      console.log(`[API Response] ${response.config.method.toUpperCase()} ${response.config.url}`, response.data);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log error in development
    errorHandler.logError(error, 'Response Interceptor');

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
      const url = originalRequest?.url || '';
      const isAuthEndpoint =
        url.includes(ENDPOINTS.AUTH.LOGIN) ||
        url.includes(ENDPOINTS.AUTH.REGISTER) ||
        url.includes(ENDPOINTS.AUTH.FORGOT_PASSWORD) ||
        url.includes(ENDPOINTS.AUTH.RESET_PASSWORD) ||
        url.includes(ENDPOINTS.AUTH.VERIFY_AGE);
      
      const hasTokenInManager = !!tokenManager.getAccessToken();
      const tokenWasSent = !!originalRequest.headers?.Authorization;

      // Only clear tokens if we actually sent a token and it was rejected
      // If we didn't send a token, the 401 is expected but we shouldn't log out
      if (hasTokenInManager && tokenWasSent && !isAuthEndpoint) {
        // Clear only the space whose token was actually rejected. Calling
        // clearTokens() with no argument wipes admin *and* customer tokens, so
        // one expired admin session used to sign the shopper out too - the two
        // are meant to stay independent.
        tokenManager.clearTokens(tokenManager.getCurrentSpace());
        window.dispatchEvent(new Event('roleUpdated'));
      }
      return Promise.reject(error);
    }

    // Handle other errors
    return Promise.reject(error);
  }
);

export default axiosInstance;
